import { useState, useEffect, useCallback } from "react";
import { X, Gift, Sparkles, Star, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const BOX_REWARDS = [
  { id: 1, label: "50 XP", reward: { type: "xp", amount: 50 }, rarity: "common" },
  { id: 2, label: "1,000 XP", reward: { type: "xp", amount: 1000 }, rarity: "uncommon" },
  { id: 3, label: "2,000 XP", reward: { type: "xp", amount: 2000 }, rarity: "rare" },
  { id: 4, label: "Wrapped Fruit", reward: { type: "item", item: "wrapped_fruit", amount: 1 }, rarity: "uncommon", emoji: "🎁" },
  { id: 5, label: "Disco Fruit", reward: { type: "item", item: "disco_fruit", amount: 1 }, rarity: "rare", emoji: "🪩" },
  { id: 6, label: "Bomb", reward: { type: "item", item: "bomb", amount: 1 }, rarity: "uncommon", emoji: "💣" },
  { id: 7, label: "5 Stars", reward: { type: "stars", amount: 5 }, rarity: "uncommon" },
  { id: 8, label: "10 Stars", reward: { type: "stars", amount: 10 }, rarity: "rare" },
  { id: 9, label: "Extra Moves", reward: { type: "item", item: "extra_moves", amount: 1 }, rarity: "common", emoji: "🔄" },
  { id: 10, label: "Shuffle", reward: { type: "item", item: "shuffle", amount: 1 }, rarity: "common", emoji: "🔀" },
];

// Weighted random: common = 50%, uncommon = 30%, rare = 20%
const RARITY_WEIGHTS = { common: 50, uncommon: 30, rare: 20 };

function pickReward() {
  const roll = Math.random() * 100;
  let target;
  if (roll < RARITY_WEIGHTS.common) target = "common";
  else if (roll < RARITY_WEIGHTS.common + RARITY_WEIGHTS.uncommon) target = "uncommon";
  else target = "rare";
  
  const pool = BOX_REWARDS.filter(r => r.rarity === target);
  return pool[Math.floor(Math.random() * pool.length)];
}

const RARITY_COLORS = {
  common: "from-slate-400 to-slate-500",
  uncommon: "from-emerald-400 to-emerald-600",
  rare: "from-purple-400 to-purple-600",
};

const RARITY_GLOW = {
  common: "shadow-slate-400/30",
  uncommon: "shadow-emerald-400/30",
  rare: "shadow-purple-400/40",
};

export function DailyBoxReward({ isOpen, onClose, onReward, lastSpinDate }) {
  const [phase, setPhase] = useState("idle"); // idle, shaking, opening, revealed
  const [selectedReward, setSelectedReward] = useState(null);
  const [canOpen, setCanOpen] = useState(true);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (lastSpinDate) {
      const today = new Date().toDateString();
      const last = new Date(lastSpinDate).toDateString();
      setCanOpen(today !== last);
    } else {
      setCanOpen(true);
    }
  }, [lastSpinDate, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setPhase("idle");
      setSelectedReward(null);
      setParticles([]);
    }
  }, [isOpen]);

  const openBox = useCallback(() => {
    if (phase !== "idle" || !canOpen) return;

    const reward = pickReward();
    setSelectedReward(reward);

    // Phase 1: Shake
    setPhase("shaking");

    // Phase 2: Open with particles
    setTimeout(() => {
      setPhase("opening");
      // Generate burst particles
      const newParticles = Array.from({ length: 16 }, (_, i) => ({
        id: i,
        angle: (i / 16) * 360,
        distance: 60 + Math.random() * 80,
        size: 4 + Math.random() * 8,
        color: ["#FFD700", "#FF6B6B", "#4CAF50", "#2196F3", "#9C27B0", "#FF5722"][Math.floor(Math.random() * 6)],
        delay: Math.random() * 200,
      }));
      setParticles(newParticles);
    }, 1500);

    // Phase 3: Reveal reward
    setTimeout(() => {
      setPhase("revealed");
      setCanOpen(false);
      if (onReward) {
        onReward(reward.reward);
      }
    }, 2500);
  }, [phase, canOpen, onReward]);

  const handleClose = () => {
    if (phase === "shaking" || phase === "opening") return;
    setPhase("idle");
    setSelectedReward(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" data-testid="daily-box-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />

      <Card className="relative z-10 w-full max-w-sm bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-2 border-amber-500/50 shadow-2xl overflow-hidden">
        {/* Close */}
        <button
          onClick={handleClose}
          disabled={phase === "shaking" || phase === "opening"}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white z-20 disabled:opacity-30"
          data-testid="daily-box-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center pt-6 pb-2 px-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-amber-400 animate-pulse" />
            <h2 className="text-xl font-bold text-white">Daily Reward Box</h2>
            <Sparkles className="w-5 h-5 text-amber-400 animate-pulse" />
          </div>
          <p className="text-slate-400 text-sm">
            {canOpen ? "Tap to open your daily reward!" : "Come back tomorrow!"}
          </p>
        </div>

        {/* Box Area */}
        <div className="relative flex items-center justify-center py-10 min-h-[240px]">
          {/* Particle burst */}
          {(phase === "opening" || phase === "revealed") && particles.map(p => (
            <div
              key={p.id}
              className="absolute rounded-full"
              style={{
                width: p.size,
                height: p.size,
                backgroundColor: p.color,
                left: "50%",
                top: "50%",
                transform: phase === "opening" || phase === "revealed"
                  ? `translate(-50%, -50%) translate(${Math.cos(p.angle * Math.PI / 180) * p.distance}px, ${Math.sin(p.angle * Math.PI / 180) * p.distance}px) scale(0)`
                  : "translate(-50%, -50%)",
                transition: `all 1s ease-out ${p.delay}ms`,
                opacity: phase === "revealed" ? 0 : 1,
              }}
            />
          ))}

          {/* The Box */}
          {phase !== "revealed" && (
            <button
              onClick={openBox}
              disabled={!canOpen || phase !== "idle"}
              className={`relative transition-all duration-300 ${
                phase === "shaking" ? "animate-box-shake" : ""
              } ${phase === "opening" ? "scale-110 opacity-0" : ""} ${
                !canOpen ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:scale-105 active:scale-95"
              }`}
              data-testid="daily-box-open"
            >
              {/* Box body */}
              <div className="w-32 h-28 bg-gradient-to-b from-amber-600 to-amber-800 rounded-xl border-2 border-amber-500 shadow-lg shadow-amber-500/20 relative overflow-hidden">
                {/* Lid */}
                <div className={`absolute -top-3 -left-2 -right-2 h-10 bg-gradient-to-b from-amber-500 to-amber-600 rounded-t-xl border-2 border-amber-400 flex items-center justify-center transition-transform duration-500 origin-bottom ${
                  phase === "shaking" ? "animate-lid-wobble" : ""
                }`}>
                  <div className="w-8 h-4 bg-amber-400 rounded-full border border-amber-300" />
                </div>
                {/* Question mark */}
                <div className="flex items-center justify-center h-full pt-4">
                  <span className="text-4xl font-black text-amber-300/60">?</span>
                </div>
                {/* Glow effect */}
                {phase === "shaking" && (
                  <div className="absolute inset-0 bg-gradient-to-t from-amber-400/20 to-transparent animate-pulse" />
                )}
              </div>
              {/* Shadow */}
              <div className="mx-auto mt-2 w-28 h-3 bg-black/30 rounded-full blur-sm" />
            </button>
          )}

          {/* Revealed Reward */}
          {phase === "revealed" && selectedReward && (
            <div className="text-center animate-reward-pop" data-testid="daily-box-reward">
              {/* Glow background */}
              <div className={`absolute inset-0 bg-gradient-radial ${RARITY_COLORS[selectedReward.rarity]} opacity-10 blur-3xl`} />
              
              {/* Reward icon */}
              <div className={`w-24 h-24 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${RARITY_COLORS[selectedReward.rarity]} flex items-center justify-center shadow-2xl ${RARITY_GLOW[selectedReward.rarity]} relative`}>
                <span className="text-4xl">
                  {selectedReward.emoji || (selectedReward.reward.type === "stars" ? "⭐" : selectedReward.reward.type === "xp" ? "✨" : "🎁")}
                </span>
                {/* Sparkle ring */}
                <div className="absolute inset-0 rounded-2xl border-2 border-white/30 animate-pulse" />
              </div>

              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">
                {selectedReward.rarity} reward
              </p>
              <h3 className="text-2xl font-bold text-white mb-1">{selectedReward.label}</h3>
              <p className="text-amber-400 text-sm font-medium">Added to your inventory!</p>
            </div>
          )}
        </div>

        {/* Bottom button */}
        <div className="px-6 pb-6">
          {phase === "revealed" ? (
            <Button
              onClick={handleClose}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:brightness-110 text-white font-bold text-lg h-12"
              data-testid="daily-box-collect"
            >
              <Gift className="w-5 h-5 mr-2" />
              Collect!
            </Button>
          ) : (
            <Button
              onClick={openBox}
              disabled={!canOpen || phase !== "idle"}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:brightness-110 text-white font-bold text-lg h-12 disabled:opacity-50"
              data-testid="daily-box-open-btn"
            >
              {!canOpen ? (
                <span className="flex items-center gap-2"><Star className="w-5 h-5" /> Come Back Tomorrow</span>
              ) : phase === "idle" ? (
                <span className="flex items-center gap-2"><Package className="w-5 h-5" /> Open Box!</span>
              ) : (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Opening...
                </span>
              )}
            </Button>
          )}
        </div>
      </Card>

      <style>{`
        @keyframes box-shake {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          10% { transform: translateX(-4px) rotate(-2deg); }
          20% { transform: translateX(4px) rotate(2deg); }
          30% { transform: translateX(-6px) rotate(-3deg); }
          40% { transform: translateX(6px) rotate(3deg); }
          50% { transform: translateX(-8px) rotate(-4deg); }
          60% { transform: translateX(8px) rotate(4deg); }
          70% { transform: translateX(-6px) rotate(-3deg); }
          80% { transform: translateX(6px) rotate(3deg); }
          90% { transform: translateX(-3px) rotate(-1deg); }
        }
        .animate-box-shake { animation: box-shake 1.5s ease-in-out; }

        @keyframes lid-wobble {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-5deg); }
          40% { transform: rotate(5deg); }
          60% { transform: rotate(-8deg); }
          80% { transform: rotate(8deg); }
        }
        .animate-lid-wobble { animation: lid-wobble 1.5s ease-in-out; }

        @keyframes reward-pop {
          0% { opacity: 0; transform: scale(0.5) translateY(20px); }
          50% { transform: scale(1.1) translateY(-5px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-reward-pop { animation: reward-pop 0.6s ease-out forwards; }
      `}</style>
    </div>
  );
}

// Hook to manage daily box state (same interface as old wheel)
export function useWheelOfFortune() {
  const [isOpen, setIsOpen] = useState(false);
  const [lastSpinDate, setLastSpinDate] = useState(() => {
    return localStorage.getItem('fruitCrush_lastWheelSpin');
  });

  const openWheel = useCallback(() => setIsOpen(true), []);
  const closeWheel = useCallback(() => setIsOpen(false), []);

  const canSpinToday = useCallback(() => {
    if (!lastSpinDate) return true;
    const today = new Date().toDateString();
    const lastSpin = new Date(lastSpinDate).toDateString();
    return today !== lastSpin;
  }, [lastSpinDate]);

  const recordSpin = useCallback(() => {
    const now = new Date().toISOString();
    setLastSpinDate(now);
    localStorage.setItem('fruitCrush_lastWheelSpin', now);
  }, []);

  return {
    isOpen,
    openWheel,
    closeWheel,
    lastSpinDate,
    canSpinToday,
    recordSpin,
  };
}
